import {
  Box,
  Button,
  Card,
  Heading,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import React from 'react'

import {
  createTranslator,
  LoadingState,
  type TranslateFn,
} from '@mangovoice/mango-ui'

import type { UserApi } from '../api/userApi'
import type {
  PbxUser,
  UserDetail,
  UserPbxAssociation,
} from '../types/user.types'
import { PbxAssociationCard } from './PbxAssociationCard'
import {
  type PhonePermissionPbx,
  PhonePermissionsSection,
} from './PhonePermissionsSection'
import { UserGeneralInfoCard } from './UserGeneralInfoCard'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PbxAssociation {
  pbxId: number
  pbxName: string
  pbxUser: PbxUser
  assignedExtensionCount?: number
  assignedDeviceCount?: number
}

export interface UserDetailViewProps {
  api: UserApi
  user: UserDetail | null
  pbxAssociations?: PbxAssociation[]
  phonePermissions?: UserDetailPhonePermissions
  isLoading: boolean
  onEdit: () => void
  onAddToPbx: () => void
  onEditPbxAssociation?: (association: PbxAssociation) => void
  onViewPbx?: (pbxId: number) => void
  onViewAssignedExtensions?: (pbxId: number) => void
  /**
   * Optional predicate gating the assigned-extensions link per PBX. When it
   * returns `false`, the card renders the count as plain text instead of a
   * clickable link. Used e.g. by web-app where navigation only works for the
   * currently-selected PBX context.
   */
  canViewAssignedExtensions?: (pbxId: number) => boolean
  onRemovePbxUser: (
    pbxId: number,
    userId: number,
    userName: string,
    assignedExtensionCount: number,
    assignedDeviceCount: number,
  ) => void
  canEdit: boolean
  canDelete: boolean
  showAddToPbxButton?: boolean
  /** The Cognito key copied to the clipboard. The copy button renders only when set. */
  cognitoKey?: string | null
  /** Called after the Cognito-key copy succeeds — e.g. to toast confirmation. */
  onCognitoKeyCopied?: () => void
  /** Called with the error when the Cognito-key copy fails — e.g. to toast a failure. */
  onCognitoKeyCopyError?: (error: unknown) => void
  onResetPassword?: () => void
  isResetPasswordLoading?: boolean
  onToggleIsActive?: () => void
  isToggleIsActiveLoading?: boolean
  t?: TranslateFn
}

export interface UserDetailPhonePermissions {
  pbxPermissions: PhonePermissionPbx[]
  isLoading?: boolean
  canEdit?: boolean
  onEditPbx?: (pbxId: number) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Convert backend pbx_users into the PbxAssociation shape used by the table. */
function toPbxAssociations(
  pbxUsers: UserPbxAssociation[] | undefined,
): PbxAssociation[] {
  if (!pbxUsers) return []
  return pbxUsers.map((pu) => ({
    pbxId: pu.pbx_id,
    pbxName: pu.pbx_name,
    pbxUser: {
      id: pu.id,
      // The nested `user` field isn't needed for display — provide a minimal stub
      user: {} as PbxUser['user'],
      sending_fax_email: pu.sending_fax_email,
      sending_fax_number: pu.sending_fax_number,
      pin: pu.pin,
      fax_title: pu.fax_title,
      user_groups: pu.user_groups,
    },
    assignedExtensionCount: pu.assigned_extension_count,
    assignedDeviceCount: pu.assigned_device_count,
  }))
}

export const UserDetailView: React.FC<UserDetailViewProps> = ({
  user,
  pbxAssociations: endpointPbxAssociations,
  phonePermissions,
  isLoading,
  onEdit,
  onAddToPbx,
  onEditPbxAssociation,
  onViewPbx,
  onViewAssignedExtensions,
  canViewAssignedExtensions,
  onRemovePbxUser,
  canEdit,
  canDelete,
  showAddToPbxButton = true,
  cognitoKey,
  onCognitoKeyCopied,
  onCognitoKeyCopyError,
  onResetPassword,
  isResetPasswordLoading,
  onToggleIsActive,
  isToggleIsActiveLoading,
  t,
}) => {
  const tr = React.useMemo(() => createTranslator(t), [t])
  const [pbxSearchTerm, setPbxSearchTerm] = React.useState('')

  const pbxAssociations = React.useMemo(() => {
    if (endpointPbxAssociations) return endpointPbxAssociations
    return toPbxAssociations(user?.pbx_users)
  }, [endpointPbxAssociations, user?.pbx_users])

  const filteredPbxAssociations = React.useMemo(() => {
    const normalizedSearch = pbxSearchTerm.trim().toLowerCase()
    if (!normalizedSearch) return pbxAssociations

    return pbxAssociations.filter((association) =>
      association.pbxName.toLowerCase().includes(normalizedSearch),
    )
  }, [pbxAssociations, pbxSearchTerm])

  if (isLoading || !user) {
    return (
      <LoadingState title={tr('users.detail.loadingUser', 'Loading user...')} />
    )
  }

  return (
    <VStack align="stretch" gap={6} width="100%">
      <UserGeneralInfoCard
        user={user}
        canEdit={canEdit}
        onEdit={onEdit}
        cognitoKey={cognitoKey}
        onCognitoKeyCopied={onCognitoKeyCopied}
        onCognitoKeyCopyError={onCognitoKeyCopyError}
        onResetPassword={onResetPassword}
        isResetPasswordLoading={isResetPasswordLoading}
        onToggleIsActive={onToggleIsActive}
        isToggleIsActiveLoading={isToggleIsActiveLoading}
        t={t}
      />

      {/* PBX Associations */}
      <Card.Root width="100%">
        <Card.Header p={6}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading as="h2" fontSize="lg" fontWeight="semibold">
              {tr('users.detail.pbxAssociations.heading', 'PBX Associations')}
            </Heading>
            {canEdit && showAddToPbxButton && (
              <Button
                size="sm"
                colorPalette="brand"
                variant="outline"
                onClick={onAddToPbx}
              >
                <Plus size={14} />
                {tr('users.detail.pbxAssociations.addToPbx', 'Add to PBX')}
              </Button>
            )}
          </Box>

          {pbxAssociations.length > 0 && (
            <Input
              mt={4}
              value={pbxSearchTerm}
              onChange={(e) => setPbxSearchTerm(e.target.value)}
              placeholder={tr(
                'users.detail.pbxAssociations.filterPlaceholder',
                'Filter PBX associations...',
              )}
              aria-label={tr(
                'users.detail.pbxAssociations.filterAriaLabel',
                'Filter PBX associations',
              )}
              bg="bg.subtle"
              borderColor="border.default"
            />
          )}
        </Card.Header>

        <Card.Body p={6}>
          {pbxAssociations.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              {tr(
                'users.detail.pbxAssociations.empty',
                'This user is not assigned to any PBX.',
              )}
            </Text>
          ) : filteredPbxAssociations.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              {tr(
                'users.detail.pbxAssociations.noMatches',
                'No PBX associations match your search.',
              )}
            </Text>
          ) : (
            <VStack align="stretch" gap={2}>
              {filteredPbxAssociations.map((assoc) => (
                <PbxAssociationCard
                  key={assoc.pbxId}
                  association={{
                    pbxId: assoc.pbxId,
                    pbxName: assoc.pbxName,
                    userId: user.id,
                    userName:
                      user.name ||
                      `${user.first_name} ${user.last_name}`.trim(),
                    sendingFaxEmail: assoc.pbxUser.sending_fax_email,
                    sendingFaxNumber: assoc.pbxUser.sending_fax_number,
                    pin: assoc.pbxUser.pin,
                    faxTitle: assoc.pbxUser.fax_title,
                    assignedExtensionCount: assoc.assignedExtensionCount,
                    assignedDeviceCount: assoc.assignedDeviceCount,
                  }}
                  canEdit={canEdit}
                  onEdit={
                    onEditPbxAssociation
                      ? () => onEditPbxAssociation(assoc)
                      : undefined
                  }
                  canDelete={canDelete}
                  onViewPbx={onViewPbx}
                  onViewAssignedExtensions={
                    canViewAssignedExtensions?.(assoc.pbxId) === false
                      ? undefined
                      : onViewAssignedExtensions
                  }
                  onRemove={onRemovePbxUser}
                  t={t}
                />
              ))}
            </VStack>
          )}
        </Card.Body>
      </Card.Root>

      {phonePermissions && (
        <PhonePermissionsSection
          pbxPermissions={phonePermissions.pbxPermissions}
          isLoading={phonePermissions.isLoading}
          canEdit={phonePermissions.canEdit ?? canEdit}
          onEditPbx={phonePermissions.onEditPbx}
          onViewPbx={onViewPbx}
          t={t}
        />
      )}
    </VStack>
  )
}
